import { ChevronDown, ChevronsUpDown, ChevronUp } from 'lucide-react'
import type * as React from 'react'
import { cn } from '../../lib/cn'
import { Checkbox } from '../atoms/Checkbox'
import { Spinner } from '../atoms/Spinner'

/**
 * DataTable — table with sort, selection, density modes, loading/empty/error states.
 * Pencil YlxWo. Composes DataTableToolbar + DataTablePagination molecules (Phase 3).
 *
 * Minimal headless API — you provide columns + rows, the table handles presentation,
 * sort clicks, selection state, row-hover/active, keyboard focus, and state variants.
 */
export type SortDirection = 'asc' | 'desc' | null

export interface DataTableColumn<T> {
  key: string
  header: React.ReactNode
  /** render cell from row */
  cell: (row: T, index: number) => React.ReactNode
  /** enable sort on this column */
  sortable?: boolean
  /** fixed width (e.g. '120px' | '25%' | '1fr'); omit for auto */
  width?: string
  /** text alignment */
  align?: 'left' | 'right' | 'center'
}

export interface DataTableProps<T> extends React.HTMLAttributes<HTMLDivElement> {
  columns: DataTableColumn<T>[]
  rows: T[]
  getRowId: (row: T) => string
  /** Density mode */
  density?: 'comfortable' | 'compact'
  /** Sort state (controlled) */
  sort?: { key: string; direction: SortDirection }
  onSortChange?: (sort: { key: string; direction: SortDirection }) => void
  /** Selection (controlled, set to undefined to disable) */
  selectedIds?: Set<string>
  onSelectionChange?: (next: Set<string>) => void
  /** Interaction state per row */
  onRowClick?: (row: T) => void
  activeRowId?: string
  /** Async states */
  loading?: boolean
  error?: React.ReactNode
  empty?: React.ReactNode
  /** Rendered when loading is true and no rows yet (skeleton). Default 5 rows. */
  loadingRowCount?: number
  /** Optional className per row — useful for styling deleted/disabled rows */
  rowClassName?: (row: T) => string | undefined
}

export function DataTable<T>({
  className,
  columns,
  rows,
  getRowId,
  density = 'comfortable',
  sort,
  onSortChange,
  selectedIds,
  onSelectionChange,
  onRowClick,
  activeRowId,
  loading,
  error,
  empty,
  loadingRowCount = 5,
  rowClassName,
  ...props
}: DataTableProps<T>) {
  const selectable = !!selectedIds && !!onSelectionChange
  const allSelected =
    selectable && rows.length > 0 && rows.every((r) => selectedIds!.has(getRowId(r)))
  const someSelected = selectable && !allSelected && rows.some((r) => selectedIds!.has(getRowId(r)))

  const toggleAll = () => {
    if (!onSelectionChange) return
    if (allSelected) onSelectionChange(new Set())
    else onSelectionChange(new Set(rows.map(getRowId)))
  }

  const toggleRow = (id: string) => {
    if (!onSelectionChange || !selectedIds) return
    const next = new Set(selectedIds)
    next.has(id) ? next.delete(id) : next.add(id)
    onSelectionChange(next)
  }

  const clickSort = (col: DataTableColumn<T>) => {
    if (!col.sortable || !onSortChange) return
    const cur = sort?.key === col.key ? sort.direction : null
    const next: SortDirection = cur === 'asc' ? 'desc' : cur === 'desc' ? null : 'asc'
    onSortChange({ key: col.key, direction: next })
  }

  const rowPad = density === 'compact' ? 'h-10' : 'h-14'
  const headPad = density === 'compact' ? 'h-10' : 'h-12'

  return (
    <div
      className={cn(
        'relative rounded-lg border border-ia-border bg-card overflow-hidden',
        className,
      )}
      {...props}
    >
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead className="bg-ia-elevated">
            <tr className={cn('text-left', headPad)}>
              {selectable && (
                <th className="w-10 px-3">
                  <Checkbox
                    aria-label="Select all rows"
                    checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                    onCheckedChange={toggleAll}
                  />
                </th>
              )}
              {columns.map((col) => {
                const isSorted = sort?.key === col.key && sort.direction !== null
                const dir = isSorted ? sort!.direction : null
                return (
                  <th
                    key={col.key}
                    style={{ width: col.width }}
                    className={cn(
                      'px-4 text-caption font-semibold uppercase tracking-wider text-ia-text-tertiary whitespace-nowrap',
                      col.align === 'right' && 'text-right',
                      col.align === 'center' && 'text-center',
                    )}
                  >
                    {col.sortable ? (
                      <button
                        type="button"
                        onClick={() => clickSort(col)}
                        className="inline-flex items-center gap-1 hover:text-ia-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
                      >
                        {col.header}
                        {dir === 'asc' ? (
                          <ChevronUp size={12} />
                        ) : dir === 'desc' ? (
                          <ChevronDown size={12} />
                        ) : (
                          <ChevronsUpDown size={12} className="opacity-50" />
                        )}
                      </button>
                    ) : (
                      col.header
                    )}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 ? (
              Array.from({ length: loadingRowCount }).map((_, i) => (
                <tr key={i} className={cn('border-t border-ia-border', rowPad)}>
                  {selectable && (
                    <td className="px-3">
                      <div className="h-4 w-4 rounded-sm bg-ia-elevated animate-pulse" />
                    </td>
                  )}
                  {columns.map((c) => (
                    <td key={c.key} className="px-4">
                      <div
                        className="h-4 rounded bg-ia-elevated animate-pulse"
                        style={{ width: `${60 + Math.random() * 30}%` }}
                      />
                    </td>
                  ))}
                </tr>
              ))
            ) : rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length + (selectable ? 1 : 0)}
                  className="px-4 py-12 text-center"
                >
                  {error ? (
                    <div className="text-ia-error">{error}</div>
                  ) : (
                    (empty ?? <div className="text-ia-text-tertiary">No rows to display</div>)
                  )}
                </td>
              </tr>
            ) : (
              rows.map((row, i) => {
                const id = getRowId(row)
                const isSelected = selectedIds?.has(id)
                const isActive = activeRowId === id
                return (
                  <tr
                    key={id}
                    onClick={() => onRowClick?.(row)}
                    className={cn(
                      'border-t border-ia-border transition-colors',
                      rowPad,
                      onRowClick && 'cursor-pointer',
                      isActive ? 'bg-ia-surface-selected' : 'hover:bg-ia-surface-hover',
                      isSelected && 'bg-ia-surface-selected',
                      rowClassName?.(row),
                    )}
                  >
                    {selectable && (
                      <td className="px-3" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          aria-label={`Select row ${i + 1}`}
                          checked={isSelected}
                          onCheckedChange={() => toggleRow(id)}
                        />
                      </td>
                    )}
                    {columns.map((col) => (
                      <td
                        key={col.key}
                        className={cn(
                          'px-4 text-body-sm text-ia-text',
                          col.align === 'right' && 'text-right tabular-nums',
                          col.align === 'center' && 'text-center',
                        )}
                      >
                        {col.cell(row, i)}
                      </td>
                    ))}
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>
      {loading && rows.length > 0 && (
        <div className="absolute top-3 right-3">
          <Spinner size="sm" label="Refreshing" />
        </div>
      )}
    </div>
  )
}
